import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import { FeatureSwitch, FeatureSwitchesDefaultValues, FeatureSwitchMap } from './featureSwitches'
import { loadFeatures } from '../../api/features/featuresApi'
import { FlowMetaData } from '../../@types/types'
import { getDefaultLogger } from '../../services/logger/logger'
import Spacer from '../../components/common/containers/spacer/Spacer'
import { useAppSelector } from '../../app/hooks'
import { InitState } from '../../redux/reducers/root/initialState'

export type FeaturesContextType = {
  isActive: (feature: FeatureSwitch) => boolean
}

const FeaturesContext = React.createContext<FeaturesContextType | undefined>(undefined)
type FeatureSwitchProviderProps = {
  featureSwitches?: FeatureSwitchMap
  flowMetadata?: FlowMetaData
  /**
   * When true, triggers a feature fetch even if `flowMetadata` is missing.
   * Use this only when features must be loaded without flow context and you accept
   * that the provider will block rendering (showing `<Spacer />`) until the fetch completes.
   */
  loadWithoutMetadata?: boolean
}

function FeaturesSwitchProviderInternal(props: PropsWithChildren<FeatureSwitchProviderProps>) {
  const [features, setFeatures] = useState<FeatureSwitchMap>()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (props.featureSwitches) {
      setFeatures(props.featureSwitches)
      setIsLoading(false)
      return
    }

    if (props.flowMetadata || props.loadWithoutMetadata) {
      loadFeatures(props.flowMetadata)
        .then((loadedFeatures) => {
          setFeatures(loadedFeatures)
          setIsLoading(false)
          return undefined
        })
        .catch((e) => {
          getDefaultLogger().warn('features could not be loaded, use default values', e)
          setFeatures(FeatureSwitchesDefaultValues)
          setIsLoading(false)
        })
    } else {
      getDefaultLogger().info('No flow metadata available, using default feature values')
      setFeatures(FeatureSwitchesDefaultValues)
      setIsLoading(false)
    }
  }, [props.featureSwitches, props.flowMetadata, props.loadWithoutMetadata])

  const isActive = useCallback(
    (feature: FeatureSwitch) => {
      return features?.[feature] ?? FeatureSwitchesDefaultValues[feature] ?? false
    },
    [features]
  )
  if (isLoading || !features) {
    return <Spacer />
  }

  return <FeaturesContext.Provider value={{ isActive }} {...props} />
}

/**
 * Main features provider component that automatically includes flow metadata.
 *
 * This component waits for flow metadata to be available before rendering
 * and ensures features are ALWAYS loaded with proper flow context.
 *
 * During authentication flows (when flow metadata is not available), it falls back
 * to default feature values to prevent blocking the UI.
 */
function FeaturesSwitchProvider(props: PropsWithChildren<Omit<FeatureSwitchProviderProps, 'flowMetadata'>>) {
  const flow = useAppSelector((state: InitState) => state.flowFeature.flow)
  const flowInformation = flow?.flowInformation
  const flowMetadata: FlowMetaData | undefined = useMemo(() => {
    if (flowInformation?.metaData) {
      return {
        category: flowInformation.metaData.category,
        transaction: flowInformation.metaData.transaction,
        segmentation: flowInformation.metaData.segmentation,
      }
    }

    // For e2e tests, check cookies for flow metadata
    if (typeof window !== 'undefined') {
      try {
        const cookies = document.cookie.split(';')
        const e2eFlowMetadataCookie = cookies.find((cookie) => cookie.trim().startsWith('e2e_flow_metadata='))
        if (e2eFlowMetadataCookie) {
          const cookieValue = e2eFlowMetadataCookie.split('=')[1]
          return JSON.parse(decodeURIComponent(cookieValue))
        }
      } catch (e) {
        // Ignore cookie errors
        getDefaultLogger().warn('Failed to read e2e flow metadata from cookies:', e)
      }
    }

    return undefined
  }, [flowInformation?.metaData])

  // If featureSwitches are provided directly (e.g., in tests), use them without requiring flow metadata
  if (props.featureSwitches) {
    return <FeaturesSwitchProviderInternal {...props} flowMetadata={flowMetadata} />
  }

  return <FeaturesSwitchProviderInternal {...props} flowMetadata={flowMetadata} />
}

/**
 * Use this hook in your component that you want to apply a feature switch on. Like:
 *
 * const {isActive: isFeatureForUnitTestEnabled} = useFeatureSwitch(FeatureSwitch.FEATURE_FOR_UNIT_TESTS)
 * ...
 * return <div>{isFeatureForUnitTestEnabled?'feature enabled':'feature disabled'}</div>
 *
 * The feature switch will automatically include flow metadata (category, transaction, segmentation)
 * based on the current flow context from the Redux store.
 *
 * In case your feature switch is not applicable, use the second parameter:
 *
 * const {isActive: isFeatureForUnitTestEnabled} = useFeatureSwitch(FeatureSwitch.FEATURE_FOR_UNIT_TESTS, false)
 * ...
 * return <div>Feature is disabled</div>
 *
 * In your unit test, provide the feature switch as a parameter to the render method from test-utils, like this:
 * import { render } from '../../../../utils/test-utils'
 * ...
 * render(<MyComponent {...props} />,{ featureSwitches:{[FeatureSwitch.FEATURE_FOR_UNIT_TESTS]:true} })
 *
 * Note: Flow metadata is automatically provided by FeaturesSwitchProvider and doesn't need to be
 * manually specified in tests - it's derived from the Redux store context.
 */
function useFeatureSwitch(feature: FeatureSwitch, enabled: boolean = true) {
  const context = useFeaturesContext()

  return {
    isActive: enabled ? context.isActive(feature) : false,
  }
}

export function useFeaturesContext() {
  const context = React.useContext(FeaturesContext)
  if (!context) {
    throw new Error(`useFeatureSwitch must be used within a FeaturesSwitchProvider`)
  }
  return context
}

export { FeaturesSwitchProvider, useFeatureSwitch }
